/**
 * Какие значки блока «Баннеры платёжных систем» (`payment_banners`)
 * конструктор предлагает автору, а какие сняты (WEB-2960 — кабинетная часть
 * WEB-2314, вариант А).
 *
 * Почему PayPal снят: оплата принимает только карту — `payment_method_types:
 * ['card']` (WEB-1449, решение PM; `backend/src/payments/providers/stripe/
 * stripe-payment.provider.ts`). Значок PayPal обещал покупателю способ,
 * которого на странице оплаты нет. Серверная сторона того же решения —
 * `backend/src/ai-funnels/block-schema/paywall.ts` (модели PayPal не
 * предлагается, старые воронки с ним схема по-прежнему принимает).
 *
 * Таблица `Record<PaymentBannerProvider, …>`: новое значение типа без строки
 * здесь не соберётся — предложенные + снятые всегда покрывают тип целиком.
 *
 * Публичная отрисовка (`core-blocks/PaymentBannersRenderer.tsx`) этот модуль
 * НЕ импортирует — она в первом экране покупателя, где на счету каждый байт;
 * снятое значение там отсеяно на месте, а совпадение двух концов держит тест
 * доли (`PaymentBannersRenderer.test.tsx` перебирает `RETIRED_PAYMENT_BANNERS`).
 */
import type { PaymentBannerProvider } from '@/store/editorSlice';

const PAYMENT_BANNERS: Record<
  PaymentBannerProvider,
  { label: string; offered: boolean }
> = {
  visa: { label: 'Visa', offered: true },
  mastercard: { label: 'Mastercard', offered: true },
  apple_pay: { label: 'Apple Pay', offered: true },
  google_pay: { label: 'Google Pay', offered: true },
  paypal: { label: 'PayPal', offered: false },
};

const ENTRIES = Object.entries(PAYMENT_BANNERS) as Array<
  [PaymentBannerProvider, { label: string; offered: boolean }]
>;

/** Значки, которые панель блока предлагает автору (порядок — как в таблице). */
export const PAYMENT_BANNER_OPTIONS: Array<{
  value: PaymentBannerProvider;
  label: string;
}> = ENTRIES.filter(([, v]) => v.offered).map(([value, v]) => ({
  value,
  label: v.label,
}));

/** Снятые значки: в данных старых воронок могут лежать, покупателю не рисуются. */
export const RETIRED_PAYMENT_BANNERS: PaymentBannerProvider[] = ENTRIES.filter(
  ([, v]) => !v.offered,
).map(([value]) => value);

/**
 * WEB-2963: ОДИН набор снятых способов оплаты на оба блока, где значок обещает
 * способ оплаты, — «Баннеры платёжных систем» и «Значки доверия». Выведен из
 * таблицы выше (флаг `offered`), второго списка руками нет: снимешь способ здесь —
 * он уйдёт из обоих блоков. Совпадение держит сторож
 * `__tests__/retiredPaymentMethods.twoEnds.web2963.guard.test.tsx`.
 */
export const RETIRED_PAYMENT_METHODS: ReadonlySet<string> = new Set<string>(
  RETIRED_PAYMENT_BANNERS,
);

const RETIRED = RETIRED_PAYMENT_METHODS;

/** Набор значков без снятых; порядок автора и незнакомые значения сохраняются. */
export function withoutRetiredPaymentBanners(
  banners: readonly string[] | undefined,
): string[] {
  return (banners ?? []).filter((b) => !RETIRED.has(b));
}

/** В данных блока есть снятый значок — панель показывает владельцу пометку. */
export function hasRetiredPaymentBanners(
  banners: readonly string[] | undefined,
): boolean {
  return (banners ?? []).some((b) => RETIRED.has(b));
}

/**
 * WEB-2451, возврат 1: ключ сравнения значка «Значков доверия» со снятым
 * набором — обрезать пробелы по краям и привести к нижнему регистру. Тот же
 * ключ, что `legacyValueKey` сервера (WEB-2916,
 * `backend/src/ai-funnels/block-schema/legacy-enum-value.ts`): в этом блоке
 * свободный текст, и «PayPal», « paypal», «PAYPAL » — то же обещание PayPal
 * покупателю. Совпадение с сервером держит сторож
 * `__tests__/retiredPaymentMethods.twoEnds.web2963.guard.test.tsx`.
 * «Платёжные баннеры» — перечисление, там сравнение точное.
 */
export function retiredMethodKey(value: string): string {
  return value.trim().toLowerCase();
}

function isRetiredTrustBadge(value: unknown): boolean {
  return typeof value === 'string' && RETIRED.has(retiredMethodKey(value));
}

/**
 * WEB-2963: значки блока «Значки доверия» (`trust_badges`) без снятых способов
 * оплаты в любом регистре и с пробелами по краям; порядок автора и прочие
 * значки (SSL, Norton…) сохраняются.
 */
export function withoutRetiredTrustBadges(
  items: readonly string[] | undefined,
): string[] {
  return (items ?? []).filter((b) => !isRetiredTrustBadge(b));
}

/** WEB-2963: в «Значках доверия» старой воронки лежит снятый способ оплаты. */
export function hasRetiredTrustBadges(
  items: readonly string[] | undefined,
): boolean {
  return (items ?? []).some(isRetiredTrustBadge);
}

/**
 * WEB-2320: способы оплаты, которые на одном экране стоят И в «Значках доверия»,
 * И в «Платёжных баннерах» — посетитель увидит такой значок дважды. Возвращает
 * подписи (в порядке таблицы баннеров), снятые способы не считает: их не видно.
 * Панели обоих блоков показывают по нему предупреждение, не запрет.
 */
export function paymentBadgesOverlap(
  screenBlocks:
    | ReadonlyArray<{ type?: string; trustBadgeItems?: readonly string[]; banners?: readonly string[] }>
    | undefined,
): string[] {
  const trust = new Set<string>();
  const banners = new Set<string>();
  for (const b of screenBlocks ?? []) {
    if (b.type === 'trust_badges') {
      for (const v of b.trustBadgeItems ?? []) trust.add(v);
    } else if (b.type === 'payment_banners') {
      for (const v of b.banners ?? []) banners.add(v);
    }
  }
  return PAYMENT_BANNER_OPTIONS.filter(
    (o) => trust.has(o.value) && banners.has(o.value),
  ).map((o) => o.label);
}
